import './RequestAccessModal.scss';
import { FormController } from '@form-ts/core';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { useCountryList } from 'src/modules/common/hooks/useCountryList';
import { CircleCheckFilledIcon } from 'src/modules/common/icons/CircleCheckFilledIcon';
import { SearchableSelectField } from 'src/modules/form/components/SearchableSelectField';
import { TextField } from 'src/modules/form/components/TextField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { REQUEST_ACCESS_FORM_SCHEMA } from 'src/modules/login/constants/schema';
import { RequestAccessFormData } from 'src/modules/login/types/RequestAccessFormData';

type Props = {
  readonly onClose: () => void;
  readonly onSubmit: (form: FormController<RequestAccessFormData, FormError>) => void;
};

export const RequestAccessModal = ({ onClose, onSubmit }: Props): React.ReactElement => {
  const intl = useIntl();
  const countryCodes = useCountryList();

  const requestAccessForm = useForm<RequestAccessFormData, FormError>('requestAccess', {
    reinitialize: false,
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      countryCode: 'CHE',
    },
  });
  useFormValidator(requestAccessForm, REQUEST_ACCESS_FORM_SCHEMA);

  const isSubmitting = useFormWatch(requestAccessForm, requestAccessForm.submitting.get);
  const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(requestAccessForm);
  }, [requestAccessForm, onSubmit]);

  const isSubmitted = useFormWatch(requestAccessForm, requestAccessForm.submitSucceded.get);
  if (isSubmitted) {
    return (
      <div className="bp-request-access-modal">
        <ModalHeader
          label={intl.formatMessage({ id: 'login/requestAccess/modal/header' })}
          handleClose={onClose}
        />

        <ModalBody>
          <div className="bp-request-access-modal__success-wrapper">
            <div className="bp-request-access-modal__success-wrapper-icon">
              <CircleCheckFilledIcon/>
            </div>

            <p className="bp-project-list-import-selector__success-text">
              <FormattedMessage id="login/requestAccess/modal/success"/>
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            kind="secondary"
            autoFocus={true}
            onClick={onClose}
            fullWidth={true}
          >
            <FormattedMessage id="login/requestAccess/modal/close"/>
          </Button>

          <div/>
        </ModalFooter>
      </div>
    );
  }

  return (
    <form className="bp-request-access-modal" onSubmit={handleSubmit}>
      <ModalHeader
        label={intl.formatMessage({ id: 'login/requestAccess/modal/header' })}
        handleClose={onClose}
      />

      <ModalBody>
        <div className="bp-request-access-modal__form-content">
          <div className="bp-request-access-modal__form-field">
            <TextField
              field={requestAccessForm.field.at('firstName')}
              label={intl.formatMessage({ id: 'login/requestAccess/modal/firstName' })}
              type="text"
              size="lg"
              autoComplete="name"
              placeholder={intl.formatMessage({ id: 'login/requestAccess/modal/firstName' })}
              description={null}
              maxLength={40}
            />
          </div>

          <div className="bp-request-access-modal__form-field">
            <TextField
              field={requestAccessForm.field.at('lastName')}
              label={intl.formatMessage({ id: 'login/requestAccess/modal/lastName' })}
              type="text"
              size="lg"
              autoComplete="name"
              placeholder={intl.formatMessage({ id: 'login/requestAccess/modal/lastName' })}
              description={null}
              maxLength={40}
            />
          </div>

          <div className="bp-request-access-modal__form-field">
            <TextField
              field={requestAccessForm.field.at('companyName')}
              label={intl.formatMessage({ id: 'login/requestAccess/modal/companyName' })}
              type="text"
              size="lg"
              autoComplete="organization"
              placeholder={intl.formatMessage({ id: 'login/requestAccess/modal/companyName' })}
              description={null}
              maxLength={40}
            />
          </div>

          <div className="bp-request-access-modal__form-field">
            <TextField
              field={requestAccessForm.field.at('email')}
              label={intl.formatMessage({ id: 'login/requestAccess/modal/email' })}
              type="text"
              size="lg"
              autoComplete="email"
              placeholder={intl.formatMessage({ id: 'login/requestAccess/modal/email' })}
              description={null}
              maxLength={40}
            />
          </div>

          <div className="bp-request-access-modal__form-field">
            <SearchableSelectField
              size="md"
              label={intl.formatMessage({ id: 'basedata/building/address/country' })}
              field={requestAccessForm.field.at('countryCode')}
              options={countryCodes}
              formatOptionLabel={(option) => intl.formatMessage({ id: `common/country/name/${option.value}` })}
              menuPlacement="top"
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          kind="secondary"
          fullWidth={true}
          onClick={onClose}
          disabled={isSubmitting}
        >
          <FormattedMessage id="login/requestAccess/modal/cancel"/>
        </Button>

        <Button
          disabled={isSubmitting}
          type="submit"
          kind="primary"
          fullWidth={true}
          autoFocus={true}
          loading={isSubmitting}
        >
          <FormattedMessage id="login/requestAccess/modal/submit"/>
        </Button>
      </ModalFooter>
    </form>
  );
};
